/**
 * ワールドの当たり判定と移動解決。
 *
 * entity を AABB / OBB のコライダに変換し、空間ハッシュで近傍だけ引く。
 * プレイヤーは AABB のまま、スイープ SAT の TOI と接触法線スライドで動かす。
 *
 * three.js には依存しない純粋モジュールなので、テストからそのまま呼べる。
 */

import type { BlockPose } from './block-lattice';
import type { BraceGeometry, Entity, PlaneGeometry, Vec3 } from './types';
import {
  bracePolyline,
  braceStripQuads,
  braceAsSlab,
  isFillGeometry,
  tessellateSlab,
  fillSideFace,
  fillSideFaceHoles,
  pathFaceHoles,
  pathLogicalFace,
  planeHasHoles,
  planeSlab,
  stairCollisionPlane,
  vec,
  ZERO_PLANE_THICKNESS,
} from './geometry';
import {
  aabbIntersectsObb,
  aabbIntersectsPrism,
  aabbMtvAabb,
  aabbMtvObb,
  aabbMtvPrism,
  blockObb,
  braceObbs,
  obbBounds,
  obbIsWorldAligned,
  prismBounds,
  rectanglePlaneObb,
  sweepAabbAgainstAabb,
  sweepAabbAgainstObb,
  sweepAabbAgainstPlaneY,
  sweepAabbAgainstPrism,
  type Mtv,
  type Obb,
  type SweepHit,
  type TrianglePrism,
} from './obb';
import { isWaterMaterial } from './materials';
import { hasRevolve, revolveSurfaceTriangles } from './revolve';
import { resolveObjectDefinition } from './object-catalog';
import { objectWorldAabb } from './object-pose';
import {
  VIEW_GRID_TILE,
  localToWorldXZ,
  snapOnYaw,
  snapYawClass,
  worldToLocalXZ,
} from './view-grid';

export interface Aabb {
  min: Vec3;
  max: Vec3;
}

/** プレイヤーの当たり判定サイズ（足元基準の直方体） */
export interface BodySize {
  width: number;
  height: number;
}

const AXES = ['x', 'y', 'z'] as const;
type Axis = (typeof AXES)[number];

/** 接触しているだけの面で引っかからないようにする許容誤差 */
const TOUCH_EPSILON = 1e-4;
/** 衝突後に残す隙間。0 だと次フレームで内側判定になりうる。 */
const SKIN = 1e-3;

/** 1 entity あたりの当たり判定ボックス数の上限（暴走防止） */
export const MAX_BOXES_PER_ENTITY = 1024;
/** 空間ハッシュのセル辺長 */
export const CELL_SIZE = 4;
/** 1 entity が占有できるセル数の上限。超えたら「巨大オブジェクト」扱い。 */
const MAX_CELLS_PER_ENTITY = 4096;

/* ------------------------------------------------------------------ */
/* AABB ヘルパー                                                       */
/* ------------------------------------------------------------------ */

export function boundsOf(points: Vec3[]): Aabb {
  const min: Vec3 = { x: Infinity, y: Infinity, z: Infinity };
  const max: Vec3 = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const p of points) {
    for (const axis of AXES) {
      if (p[axis] < min[axis]) min[axis] = p[axis];
      if (p[axis] > max[axis]) max[axis] = p[axis];
    }
  }
  return { min, max };
}

export function unionBounds(boxes: readonly Aabb[]): Aabb | null {
  if (boxes.length === 0) return null;
  const min = { ...boxes[0].min };
  const max = { ...boxes[0].max };
  for (const box of boxes) {
    for (const axis of AXES) {
      if (box.min[axis] < min[axis]) min[axis] = box.min[axis];
      if (box.max[axis] > max[axis]) max[axis] = box.max[axis];
    }
  }
  return { min, max };
}

export function aabbIntersects(a: Aabb, b: Aabb, epsilon = 0): boolean {
  for (const axis of AXES) {
    if (a.min[axis] > b.max[axis] - epsilon) return false;
    if (a.max[axis] < b.min[axis] + epsilon) return false;
  }
  return true;
}

/** 範囲破壊の箱で、面上の2点でも面に触れるものを消すための最小辺長 */
export const MIN_RANGE_DESTROY_EDGE = 0.01;
/** 範囲破壊の角スナップ。視点グリッドのマスの 1/2。 */
export const RANGE_DESTROY_SNAP = VIEW_GRID_TILE / 2;

/**
 * 2点を対角とする、指定 yaw の直方体。
 * 角はグリッドマスの 1/2 にスナップし、各軸が `minEdge` 未満なら中心を保って厚みを足す。
 */
export function rangeDestroyPose(
  start: Vec3,
  end: Vec3,
  yaw: number,
  minEdge = MIN_RANGE_DESTROY_EDGE
): BlockPose {
  return rangeDestroyBox(
    snapOnYaw(start, yaw, RANGE_DESTROY_SNAP),
    snapOnYaw(end, yaw, RANGE_DESTROY_SNAP),
    yaw,
    minEdge
  );
}

/** すでにスナップ済みの2点から範囲破壊の箱を作る。 */
export function rangeDestroyBox(
  start: Vec3,
  end: Vec3,
  yaw: number,
  minEdge = MIN_RANGE_DESTROY_EDGE
): BlockPose {
  const localA = worldToLocalXZ(start.x, start.z, yaw);
  const localB = worldToLocalXZ(end.x, end.z, yaw);
  const min = {
    x: Math.min(localA.x, localB.x),
    y: Math.min(start.y, end.y),
    z: Math.min(localA.z, localB.z),
  };
  const max = {
    x: Math.max(localA.x, localB.x),
    y: Math.max(start.y, end.y),
    z: Math.max(localA.z, localB.z),
  };
  for (const axis of AXES) {
    const span = max[axis] - min[axis];
    if (span >= minEdge) continue;
    const extra = (minEdge - span) / 2;
    min[axis] -= extra;
    max[axis] += extra;
  }
  const world = localToWorldXZ((min.x + max.x) / 2, (min.z + max.z) / 2, yaw);
  return {
    origin: { x: world.x, y: (min.y + max.y) / 2, z: world.z },
    size: { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z },
    yaw,
    yawClass: snapYawClass(yaw),
  };
}

export function poseVolume(pose: BlockPose): number {
  return Math.max(0, pose.size.x) * Math.max(0, pose.size.y) * Math.max(0, pose.size.z);
}

/** entity の当たり判定 AABB が直方体（グリッド向きの OBB）と重なるか */
export function entityOverlapsPose(entity: CollisionEntity, pose: BlockPose): boolean {
  return poseOverlapsBoxes(entityCollisionBoxes(entity), pose);
}

/** 視線で当たる形（水・オブジェクト含む）が直方体と重なるか */
export function entityPickOverlapsPose(entity: CollisionEntity, pose: BlockPose): boolean {
  return poseOverlapsBoxes(entityPickColliders(entity).map(colliderBounds), pose);
}

function poseOverlapsBoxes(boxes: readonly Aabb[], pose: BlockPose): boolean {
  const obb = blockObb(pose);
  for (const box of boxes) {
    if (aabbIntersectsObb(box, obb, TOUCH_EPSILON)) return true;
  }
  return false;
}

/** 足元（下面の中心）位置から体の AABB を作る */
export function bodyAabb(feet: Vec3, size: BodySize): Aabb {
  const half = size.width / 2;
  return {
    min: { x: feet.x - half, y: feet.y, z: feet.z - half },
    max: { x: feet.x + half, y: feet.y + size.height, z: feet.z + half },
  };
}

function expandAabb(box: Aabb, delta: Vec3, margin: number): Aabb {
  const min = { ...box.min };
  const max = { ...box.max };
  for (const axis of AXES) {
    if (delta[axis] < 0) min[axis] += delta[axis];
    else max[axis] += delta[axis];
    min[axis] -= margin;
    max[axis] += margin;
  }
  return { min, max };
}

/* ------------------------------------------------------------------ */
/* entity → コライダ                                                   */
/* ------------------------------------------------------------------ */

export type Collider =
  | { kind: 'aabb'; box: Aabb; walkable?: boolean }
  | { kind: 'obb'; obb: Obb; bounds: Aabb; walkable?: boolean }
  | { kind: 'prism'; prism: TrianglePrism; bounds: Aabb; walkable?: boolean };

export function colliderBounds(collider: Collider): Aabb {
  return collider.kind === 'aabb' ? collider.box : collider.bounds;
}

function aabbCollider(box: Aabb, walkable = false): Collider {
  return {
    kind: 'aabb',
    box: { min: { ...box.min }, max: { ...box.max } },
    ...(walkable ? { walkable: true } : {}),
  };
}

function fromObb(obb: Obb, walkable = false): Collider {
  const bounds = obbBounds(obb);
  if (obbIsWorldAligned(obb)) return aabbCollider(bounds, walkable);
  return {
    kind: 'obb',
    obb: {
      center: { ...obb.center },
      half: { ...obb.half },
      axes: [{ ...obb.axes[0] }, { ...obb.axes[1] }, { ...obb.axes[2] }],
    },
    bounds: { min: { ...bounds.min }, max: { ...bounds.max } },
    ...(walkable ? { walkable: true } : {}),
  };
}

function prismCollider(a: Vec3, b: Vec3, c: Vec3, offset: Vec3, walkable = false): Collider | null {
  const prism: TrianglePrism = {
    a: { ...a },
    b: { ...b },
    c: { ...c },
    offset: { ...offset },
  };
  const face = vec.cross(vec.sub(b, a), vec.sub(c, a));
  if (vec.length(face) < 1e-12) return null;
  const bounds = prismBounds(prism);
  return {
    kind: 'prism',
    prism,
    bounds: { min: { ...bounds.min }, max: { ...bounds.max } },
    ...(walkable ? { walkable: true } : {}),
  };
}

function cloneCollider(collider: Collider): Collider {
  if (collider.kind === 'aabb') return aabbCollider(collider.box, collider.walkable === true);
  if (collider.kind === 'obb') return fromObb(collider.obb, collider.walkable === true);
  const next = prismCollider(
    collider.prism.a,
    collider.prism.b,
    collider.prism.c,
    collider.prism.offset,
    collider.walkable === true
  );
  return next ?? aabbCollider(collider.bounds, collider.walkable === true);
}

function markWalkable(collider: Collider): Collider {
  if (collider.kind === 'aabb') return aabbCollider(collider.box, true);
  if (collider.kind === 'obb') return fromObb(collider.obb, true);
  return (
    prismCollider(collider.prism.a, collider.prism.b, collider.prism.c, collider.prism.offset, true) ??
    collider
  );
}

function blockBoxes(geometry: { position: Vec3; size: Vec3 }): Aabb[] {
  const { position } = geometry;
  const size = geometry.size ?? { x: 1, y: 1, z: 1 };
  return [
    {
      min: { x: position.x - size.x / 2, y: position.y - size.y / 2, z: position.z - size.z / 2 },
      max: { x: position.x + size.x / 2, y: position.y + size.y / 2, z: position.z + size.z / 2 },
    },
  ];
}

/** 向き付き直方体の外接 AABB（空間ハッシュ・HUD・範囲破壊用）。 */
export function blockAabb(geometry: BlockPose): Aabb {
  const { origin, size, yaw } = geometry;
  const half = { x: size.x / 2, y: size.y / 2, z: size.z / 2 };
  const corners: Vec3[] = [];
  for (const sx of [-1, 1] as const) {
    for (const sy of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        const world = localToWorldXZ(sx * half.x, sz * half.z, yaw);
        corners.push({
          x: origin.x + world.x,
          y: origin.y + sy * half.y,
          z: origin.z + world.z,
        });
      }
    }
  }
  return boundsOf(corners);
}

/** プレイヤー AABB が向き付き直方体の実体（OBB）と重なるか */
export function blockIntersectsAabb(
  geometry: BlockPose,
  box: Aabb,
  epsilon = TOUCH_EPSILON
): boolean {
  return aabbIntersectsObb(box, blockObb(geometry), epsilon);
}

/** 回転体の閉じ面。紙の厚みの三角柱。 */
function revolveColliders(geometry: BraceGeometry): Collider[] {
  const colliders: Collider[] = [];
  for (const triangle of revolveSurfaceTriangles(geometry)) {
    if (colliders.length >= MAX_BOXES_PER_ENTITY) break;
    const face = vec.cross(vec.sub(triangle.b, triangle.a), vec.sub(triangle.c, triangle.a));
    const normal = vec.normalize(face);
    if (vec.length(normal) < 1e-9) continue;
    const offset = vec.scale(normal, ZERO_PLANE_THICKNESS);
    const shifted = {
      a: vec.sub(triangle.a, vec.scale(offset, 0.5)),
      b: vec.sub(triangle.b, vec.scale(offset, 0.5)),
      c: vec.sub(triangle.c, vec.scale(offset, 0.5)),
    };
    const prism = prismCollider(shifted.a, shifted.b, shifted.c, offset);
    if (prism) colliders.push(prism);
  }
  return colliders;
}

/** 押し出したブレース帯。各四辺形をプレーンと同じ OBB / 三角柱にする。 */
function braceStripColliders(geometry: BraceGeometry): Collider[] {
  if (geometry.holes && geometry.holes.length > 0) {
    const { points, closed } = bracePolyline(geometry);
    const n = points.length;
    const count = closed ? n : n - 1;
    const colliders: Collider[] = [];
    for (let i = 0; i < count; i++) {
      const face = pathLogicalFace(geometry, i);
      if (!face) continue;
      const holes = pathFaceHoles(geometry, face);
      colliders.push(
        ...planeColliders({
          points: face.points,
          thickness: geometry.thickness,
          normal: face.normal,
          ...(holes.length > 0 ? { holes } : {}),
        })
      );
    }
    return colliders;
  }
  const colliders: Collider[] = [];
  for (const quad of braceStripQuads(geometry)) {
    const edge = vec.sub(quad.b, quad.a);
    const offset = vec.sub(quad.d, quad.a);
    const normal = vec.normalize(vec.cross(edge, offset));
    if (vec.length(normal) < 1e-9) continue;
    colliders.push(
      ...planeColliders({
        points: [quad.a, quad.b, quad.c, quad.d],
        thickness: geometry.thickness,
        normal,
      })
    );
  }
  return colliders;
}

/** entity の当たり判定ボックス集合を作る */
export interface CollisionBoxEntity {
  id: string;
  type: 'box';
  geometry: { position: Vec3; size: Vec3 };
}

export type CollisionEntity = Entity | CollisionBoxEntity;

export function entityColliders(entity: CollisionEntity): Collider[] {
  if ('material' in entity && isWaterMaterial(entity.material)) return [];
  if (entity.type === 'object') return [];
  return entityShapeColliders(entity);
}

/** 視線で当たる形。水とオブジェクトを含み、歩行判定には使わない。 */
export function entityPickColliders(entity: CollisionEntity): Collider[] {
  if (entity.type === 'object') return objectPickColliders(entity);
  return entityShapeColliders(entity);
}

function objectPickColliders(entity: Extract<Entity, { type: 'object' }>): Collider[] {
  const bounds = resolveObjectDefinition(entity.definitionId).bounds;
  return [
    aabbCollider(
      objectWorldAabb(
        bounds,
        entity.geometry.origin,
        entity.geometry.yaw,
        entity.geometry.mount ?? '+y'
      )
    ),
  ];
}

function entityShapeColliders(entity: CollisionEntity): Collider[] {
  switch (entity.type) {
    case 'box':
      return blockBoxes(entity.geometry).map(aabbCollider);
    case 'brace':
      if (hasRevolve(entity.geometry)) return revolveColliders(entity.geometry);
      if (isFillGeometry(entity.geometry)) return planeColliders(braceAsSlab(entity.geometry));
      {
        const strip = braceStripColliders(entity.geometry);
        if (strip.length > 0) return strip;
        return braceObbs(entity.geometry).map(fromObb);
      }
    case 'stair': {
      const plane = stairCollisionPlane(entity.geometry);
      return plane ? planeColliders(plane).map(markWalkable) : [];
    }
    case 'object':
      return [];
  }
}

function planeColliders(geometry: PlaneGeometry): Collider[] {
  const obb = rectanglePlaneObb(geometry);
  if (obb) return [fromObb(obb)];
  if (planeHasHoles(geometry)) return planeHoleColliders(geometry);
  return planeTessellatedPrisms(geometry);
}

/** 穴付きは描画と同じ tessellateSlab 三角形を、ワールド AABB ではなく三角柱 OBB にする。 */
function planeHoleColliders(geometry: PlaneGeometry): Collider[] {
  if (geometry.sideHoles && geometry.sideHoles.length > 0) {
    const slab = planeSlab(geometry);
    if (!slab) return [];
    const colliders: Collider[] = [];
    colliders.push(
      ...planeColliders({
        points: slab.start,
        thickness: 0,
        normal: slab.normal,
        ...(geometry.holes && geometry.holes.length > 0 ? { holes: geometry.holes } : {}),
      })
    );
    const endHoles = geometry.holes?.map((hole) => hole.map((p) => vec.add(p, slab.offset)));
    colliders.push(
      ...planeColliders({
        points: slab.start.map((p) => vec.add(p, slab.offset)),
        thickness: 0,
        normal: slab.normal,
        ...(endHoles && endHoles.length > 0 ? { holes: endHoles } : {}),
      })
    );
    for (let i = 0; i < geometry.points.length; i++) {
      const face = fillSideFace(geometry, i);
      if (!face) continue;
      const holes = fillSideFaceHoles(geometry, face);
      colliders.push(
        ...planeColliders({
          points: face.points,
          thickness: 0,
          normal: face.normal,
          ...(holes.length > 0 ? { holes } : {}),
        })
      );
    }
    return colliders;
  }

  return planeTessellatedPrisms(geometry);
}

/** 描画と同じ tessellateSlab 三角形を厚み方向の三角柱にする。 */
function planeTessellatedPrisms(geometry: PlaneGeometry): Collider[] {
  const tessellated = tessellateSlab(geometry);
  if (!tessellated) return [];
  const { slab, origin, tangent, bitangent, vertices2, indices } = tessellated;
  const worldVerts = vertices2.map((p) =>
    vec.add(origin, vec.add(vec.scale(tangent, p.x), vec.scale(bitangent, p.y)))
  );
  const colliders: Collider[] = [];
  for (let i = 0; i < indices.length; i += 3) {
    if (colliders.length >= MAX_BOXES_PER_ENTITY) break;
    const prism = prismCollider(
      worldVerts[indices[i]],
      worldVerts[indices[i + 1]],
      worldVerts[indices[i + 2]],
      slab.offset
    );
    if (prism) colliders.push(prism);
  }
  if (colliders.length > 0) return colliders;
  const { start, offset } = slab;
  return [aabbCollider(boundsOf([...start, ...start.map((p) => vec.add(p, offset))]))];
}

export function entityCollisionBoxes(entity: CollisionEntity): Aabb[] {
  return entityColliders(entity).map(colliderBounds);
}

/* ------------------------------------------------------------------ */
/* 空間ハッシュ                                                        */
/* ------------------------------------------------------------------ */

function cellKey(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

/**
 * entity の当たり判定ボックスを格子セルに登録して近傍検索できるようにする。
 * entity 数が数千規模でも、毎フレーム全件を走査せずに済む。
 */
export class CollisionWorld {
  private readonly colliders = new Map<string, Collider[]>();
  private readonly cells = new Map<string, Set<string>>();
  private readonly keysOf = new Map<string, string[]>();
  /** セル数上限を超える巨大 entity。常に候補に含める。 */
  private readonly oversized = new Set<string>();
  /** 上からだけ着地できる水平面。クライアント視点グリッド用。 */
  oneWayFloorY: number | null = null;
  private changeRevision = 0;

  constructor(private readonly cellSize = CELL_SIZE) {}

  /** SDF など、静的コライダのキャッシュ無効化に使う単調増加値。 */
  get revision(): number {
    return this.changeRevision;
  }

  set(entity: CollisionEntity): void {
    this.setColliders(entity.id, entityColliders(entity));
  }

  /** Entity 以外の静的形状（不規則格子セル等）を直接登録する。 */
  setBoxes(id: string, boxes: readonly Aabb[]): void {
    this.setColliders(id, boxes.map(aabbCollider));
  }

  setColliders(id: string, colliders: readonly Collider[]): void {
    this.remove(id);
    if (colliders.length === 0) return;
    const stored = colliders.map(cloneCollider);
    this.colliders.set(id, stored);

    const bounds = unionBounds(stored.map(colliderBounds))!;
    const min = this.cellCoord(bounds.min);
    const max = this.cellCoord(bounds.max);
    const count =
      (max.x - min.x + 1) * (max.y - min.y + 1) * (max.z - min.z + 1);
    if (count > MAX_CELLS_PER_ENTITY) {
      this.oversized.add(id);
      this.changeRevision++;
      return;
    }

    const keys: string[] = [];
    for (let x = min.x; x <= max.x; x++) {
      for (let y = min.y; y <= max.y; y++) {
        for (let z = min.z; z <= max.z; z++) {
          const key = cellKey(x, y, z);
          let bucket = this.cells.get(key);
          if (!bucket) {
            bucket = new Set();
            this.cells.set(key, bucket);
          }
          bucket.add(id);
          keys.push(key);
        }
      }
    }
    this.keysOf.set(id, keys);
    this.changeRevision++;
  }

  remove(id: string): void {
    const changed = this.colliders.delete(id);
    this.oversized.delete(id);
    const keys = this.keysOf.get(id);
    if (!keys) {
      if (changed) this.changeRevision++;
      return;
    }
    for (const key of keys) {
      const bucket = this.cells.get(key);
      if (!bucket) continue;
      bucket.delete(id);
      if (bucket.size === 0) this.cells.delete(key);
    }
    this.keysOf.delete(id);
    this.changeRevision++;
  }

  clear(): void {
    const changed = this.colliders.size > 0;
    this.colliders.clear();
    this.cells.clear();
    this.keysOf.clear();
    this.oversized.clear();
    if (changed) this.changeRevision++;
  }

  get size(): number {
    return this.colliders.size;
  }

  /** 登録済みコライダを id ごとに列挙する（デバッグ描画用）。 */
  forEach(fn: (id: string, colliders: readonly Collider[]) => void): void {
    for (const [id, list] of this.colliders) fn(id, list);
  }

  private cellCoord(p: Vec3): { x: number; y: number; z: number } {
    return {
      x: Math.floor(p.x / this.cellSize),
      y: Math.floor(p.y / this.cellSize),
      z: Math.floor(p.z / this.cellSize),
    };
  }

  /** `area` と交差しうる entity の id を集める（広域フェーズ） */
  queryIds(area: Aabb): Set<string> {
    const min = this.cellCoord(area.min);
    const max = this.cellCoord(area.max);
    const seen = new Set<string>();

    for (let x = min.x; x <= max.x; x++) {
      for (let y = min.y; y <= max.y; y++) {
        for (let z = min.z; z <= max.z; z++) {
          const bucket = this.cells.get(cellKey(x, y, z));
          if (!bucket) continue;
          for (const id of bucket) seen.add(id);
        }
      }
    }
    for (const id of this.oversized) seen.add(id);
    return seen;
  }

  /** `area` と交差しうるコライダを集める（広域は外接 AABB） */
  query(area: Aabb, out: Collider[] = [], skipIds?: ReadonlySet<string>): Collider[] {
    out.length = 0;
    for (const id of this.queryIds(area)) {
      if (skipIds?.has(id)) continue;
      const list = this.colliders.get(id);
      if (!list) continue;
      for (const collider of list) {
        if (aabbIntersects(colliderBounds(collider), area)) out.push(collider);
      }
    }
    return out;
  }

}

/* ------------------------------------------------------------------ */
/* スイープ移動                                                        */
/* ------------------------------------------------------------------ */

function otherAxes(axis: Axis): [Axis, Axis] {
  if (axis === 'x') return ['y', 'z'];
  if (axis === 'y') return ['x', 'z'];
  return ['x', 'y'];
}

function overlaps1D(a: Aabb, b: Aabb, axis: Axis): boolean {
  return a.min[axis] < b.max[axis] - TOUCH_EPSILON && a.max[axis] > b.min[axis] + TOUCH_EPSILON;
}

/**
 * 1 軸方向へ動かせる距離を求める（スイープ AABB）。
 * 速度がいくら大きくても貫通しない。既にめり込んでいるボックスは
 * 脱出できるように無視する。
 */
export function sweepAxis(box: Aabb, delta: number, axis: Axis, boxes: readonly Aabb[]): number {
  if (delta === 0) return 0;
  const [a1, a2] = otherAxes(axis);
  let allowed = delta;

  for (const other of boxes) {
    if (!overlaps1D(box, other, a1) || !overlaps1D(box, other, a2)) continue;
    if (overlaps1D(box, other, axis)) continue; // めり込み中: 無視して脱出させる

    if (delta > 0) {
      const gap = other.min[axis] - box.max[axis];
      if (gap >= 0 && gap < allowed) allowed = Math.max(0, gap - SKIN);
    } else {
      const gap = other.max[axis] - box.min[axis];
      if (gap <= 0 && gap > allowed) allowed = Math.min(0, gap + SKIN);
    }
  }
  return allowed;
}

export interface MoveOptions {
  /** 自動で登れる段差の高さ（0 で無効） */
  stepHeight?: number;
  /** これ以下の高さには落ちない床（ワールドの地面）。null で床なし。 */
  floor?: number | null;
  /** 直前に接地していたか（段差登りの判定に使う） */
  wasOnGround?: boolean;
  /** true のとき、上からでも一方通行床（視点グリッド）をすり抜ける */
  passThroughOneWay?: boolean;
}

export interface MoveResult {
  /** 移動後の足元位置 */
  position: Vec3;
  onGround: boolean;
  hitCeiling: boolean;
  /** 水平方向で何かに当たったか */
  hitWall: boolean;
  /** 段差を自動で登ったか */
  stepped: boolean;
}

const GROUND_NORMAL_Y = 0.7;
const MAX_SLIDE_ITERS = 4;

function slideAlong(delta: Vec3, normal: Vec3): Vec3 {
  const into = vec.dot(delta, normal);
  if (into >= 0) return delta;
  return vec.sub(delta, vec.scale(normal, into));
}

function sweepCollider(box: Aabb, delta: Vec3, collider: Collider): SweepHit | null {
  if (collider.kind === 'aabb') return sweepAabbAgainstAabb(box, delta, collider.box);
  if (collider.kind === 'obb') return sweepAabbAgainstObb(box, delta, collider.obb);
  return sweepAabbAgainstPrism(box, delta, collider.prism);
}

function earliestHit(
  box: Aabb,
  delta: Vec3,
  candidates: readonly Collider[],
  floorY: number | null,
  oneWayY: number | null
): SweepHit | null {
  let best: SweepHit | null = null;
  const consider = (hit: SweepHit | null): void => {
    if (!hit) return;
    if (!best || hit.fraction < best.fraction) best = hit;
  };
  for (const collider of candidates) consider(sweepCollider(box, delta, collider));
  if (floorY !== null) consider(sweepAabbAgainstPlaneY(box, delta, floorY));
  if (oneWayY !== null) consider(sweepAabbAgainstPlaneY(box, delta, oneWayY));
  return best;
}

function colliderMtv(box: Aabb, collider: Collider): Mtv | null {
  if (collider.kind === 'aabb') return aabbMtvAabb(box, collider.box, TOUCH_EPSILON);
  if (collider.kind === 'obb') return aabbMtvObb(box, collider.obb, TOUCH_EPSILON);
  return aabbMtvPrism(box, collider.prism, TOUCH_EPSILON);
}

function depenetrate(feet: Vec3, size: BodySize, candidates: readonly Collider[]): Vec3 {
  const position = { ...feet };
  for (let i = 0; i < 3; i++) {
    const box = bodyAabb(position, size);
    let best: Mtv | null = null;
    for (const collider of candidates) {
      const mtv = colliderMtv(box, collider);
      if (mtv && (!best || mtv.depth > best.depth)) best = mtv;
    }
    if (!best) break;
    position.x += best.offset.x + best.normal.x * SKIN;
    position.y += best.offset.y + best.normal.y * SKIN;
    position.z += best.offset.z + best.normal.z * SKIN;
  }
  return position;
}

function classifyHit(
  normal: Vec3,
  flags: { onGround: boolean; hitCeiling: boolean; hitWall: boolean }
): void {
  if (normal.y >= GROUND_NORMAL_Y) flags.onGround = true;
  else if (normal.y <= -GROUND_NORMAL_Y) flags.hitCeiling = true;
  else flags.hitWall = true;
}

function applyHit(position: Vec3, remaining: Vec3, hit: SweepHit): Vec3 {
  return {
    x: position.x + remaining.x * hit.fraction + hit.normal.x * SKIN,
    y: position.y + remaining.y * hit.fraction + hit.normal.y * SKIN,
    z: position.z + remaining.z * hit.fraction + hit.normal.z * SKIN,
  };
}

function slideMove(
  feet: Vec3,
  size: BodySize,
  delta: Vec3,
  candidates: readonly Collider[],
  floorY: number | null,
  oneWayY: number | null
): { position: Vec3; remaining: Vec3; onGround: boolean; hitCeiling: boolean; hitWall: boolean } {
  let position = { ...feet };
  let remaining = { ...delta };
  const flags = { onGround: false, hitCeiling: false, hitWall: false };

  for (let i = 0; i < MAX_SLIDE_ITERS; i++) {
    if (Math.hypot(remaining.x, remaining.y, remaining.z) < 1e-8) break;
    const hit = earliestHit(bodyAabb(position, size), remaining, candidates, floorY, oneWayY);
    if (!hit) {
      position = vec.add(position, remaining);
      remaining = { x: 0, y: 0, z: 0 };
      break;
    }
    position = applyHit(position, remaining, hit);
    classifyHit(hit.normal, flags);
    remaining = slideAlong(
      {
        x: remaining.x * (1 - hit.fraction),
        y: remaining.y * (1 - hit.fraction),
        z: remaining.z * (1 - hit.fraction),
      },
      hit.normal
    );
  }

  return { position, remaining, ...flags };
}

function obbSlabAxes(obb: Obb): {
  normal: Vec3;
  halfN: number;
  u: Vec3;
  halfU: number;
  v: Vec3;
  halfV: number;
} {
  const halves = [obb.half.x, obb.half.y, obb.half.z];
  let n = 0;
  if (halves[1] < halves[n]) n = 1;
  if (halves[2] < halves[n]) n = 2;
  const axis = obb.axes[n];
  return {
    normal: axis.y >= 0 ? axis : vec.scale(axis, -1),
    halfN: halves[n],
    u: obb.axes[(n + 1) % 3],
    halfU: halves[(n + 1) % 3],
    v: obb.axes[(n + 2) % 3],
    halfV: halves[(n + 2) % 3],
  };
}

/** 歩ける斜面の上面高さ。水平投影が乗っていなければ null。 */
function pointInTriangleXZ(px: number, pz: number, a: Vec3, b: Vec3, c: Vec3): boolean {
  const v0x = c.x - a.x;
  const v0z = c.z - a.z;
  const v1x = b.x - a.x;
  const v1z = b.z - a.z;
  const v2x = px - a.x;
  const v2z = pz - a.z;
  const den = v0x * v1z - v1x * v0z;
  if (Math.abs(den) < 1e-12) return false;
  const u = (v2x * v1z - v1x * v2z) / den;
  const v = (v0x * v2z - v2x * v0z) / den;
  return u >= -TOUCH_EPSILON && v >= -TOUCH_EPSILON && u + v <= 1 + TOUCH_EPSILON;
}

function planeHeightAt(a: Vec3, b: Vec3, c: Vec3, x: number, z: number): number | null {
  const n = vec.cross(vec.sub(b, a), vec.sub(c, a));
  if (Math.abs(n.y) < 1e-6) return null;
  return a.y - (n.x * (x - a.x) + n.z * (z - a.z)) / n.y;
}

function walkableHeight(collider: Collider, x: number, z: number): number | null {
  if (!collider.walkable) return null;
  if (collider.kind === 'aabb') {
    const { min, max } = collider.box;
    if (x < min.x - TOUCH_EPSILON || x > max.x + TOUCH_EPSILON) return null;
    if (z < min.z - TOUCH_EPSILON || z > max.z + TOUCH_EPSILON) return null;
    return max.y;
  }
  if (collider.kind === 'prism') {
    const { a, b, c, offset } = collider.prism;
    const topA = offset.y >= 0 ? vec.add(a, offset) : a;
    const topB = offset.y >= 0 ? vec.add(b, offset) : b;
    const topC = offset.y >= 0 ? vec.add(c, offset) : c;
    if (!pointInTriangleXZ(x, z, topA, topB, topC)) return null;
    return planeHeightAt(topA, topB, topC, x, z);
  }
  const { normal, halfN, u, halfU, v, halfV } = obbSlabAxes(collider.obb);
  if (Math.abs(normal.y) < 1e-6) return null;
  const top = vec.add(collider.obb.center, vec.scale(normal, halfN));
  const y = top.y - (normal.x * (x - top.x) + normal.z * (z - top.z)) / normal.y;
  const delta = vec.sub({ x, y, z }, collider.obb.center);
  if (Math.abs(vec.dot(delta, u)) > halfU + TOUCH_EPSILON) return null;
  if (Math.abs(vec.dot(delta, v)) > halfV + TOUCH_EPSILON) return null;
  return y;
}

function snapToWalkable(
  feet: Vec3,
  walkables: readonly Collider[],
  snapUp: number,
  followDown: boolean
): { y: number; onGround: boolean } {
  let surface: number | null = null;
  for (const collider of walkables) {
    const height = walkableHeight(collider, feet.x, feet.z);
    if (height === null) continue;
    if (surface === null || height > surface) surface = height;
  }
  if (surface === null) return { y: feet.y, onGround: false };
  const support = surface + SKIN;
  const above = feet.y - support;
  if (above < 0 && -above <= snapUp) return { y: support, onGround: true };
  if (above >= 0 && (followDown || above <= SKIN * 4)) return { y: support, onGround: true };
  return { y: feet.y, onGround: false };
}

/**
 * 足元位置 `feet` の体を `delta` だけ動かし、衝突を解決した位置を返す。
 * スイープ SAT の TOI で止め、残り変位を接触法線に沿って滑らせる。
 */
export function moveWithCollisions(
  world: CollisionWorld,
  feet: Vec3,
  size: BodySize,
  delta: Vec3,
  options: MoveOptions = {}
): MoveResult {
  const stepHeight = options.stepHeight ?? 0;
  const floor = options.floor ?? null;
  const oneWayFloor = options.passThroughOneWay ? null : world.oneWayFloorY;
  const startedAboveOneWay =
    oneWayFloor !== null && feet.y >= oneWayFloor - TOUCH_EPSILON;

  const searchArea = expandAabb(bodyAabb(feet, size), delta, stepHeight + 1);
  const candidates = world.query(searchArea);
  const solids = candidates.filter((collider) => !collider.walkable);
  const walkables = candidates.filter((collider) => collider.walkable);
  const walkableSnapUp = Math.max(stepHeight, Math.hypot(delta.x, delta.z) * 8 + 0.05);

  let position = depenetrate(feet, size, solids);
  const origin = { ...position };
  const moved = slideMove(origin, size, delta, solids, floor, oneWayFloor);
  position = moved.position;
  let onGround = moved.onGround;
  let hitCeiling = moved.hitCeiling;
  let hitWall = moved.hitWall;
  let stepped = false;

  if (floor !== null && position.y < floor + SKIN + TOUCH_EPSILON) {
    position.y = floor;
    onGround = true;
  }
  if (startedAboveOneWay && oneWayFloor !== null && position.y < oneWayFloor + SKIN + TOUCH_EPSILON) {
    position.y = oneWayFloor;
    onGround = true;
  } else if (
    startedAboveOneWay &&
    oneWayFloor !== null &&
    Math.abs(position.y - oneWayFloor) <= TOUCH_EPSILON &&
    delta.y <= 0
  ) {
    onGround = true;
  }

  const start = { x: origin.x, y: position.y, z: origin.z };
  const traveled = Math.hypot(position.x - start.x, position.z - start.z);
  const intended = Math.hypot(delta.x, delta.z);
  const blocked = hitWall || traveled + TOUCH_EPSILON < intended;

  if (blocked && stepHeight > 0 && (onGround || options.wasOnGround)) {
    const liftHit = earliestHit(
      bodyAabb(start, size),
      { x: 0, y: stepHeight, z: 0 },
      solids,
      null,
      null
    );
    const lift = liftHit
      ? Math.max(0, liftHit.fraction * stepHeight + liftHit.normal.y * SKIN)
      : stepHeight;
    if (lift > TOUCH_EPSILON) {
      const lifted = { x: start.x, y: start.y + lift, z: start.z };
      const raised = slideMove(
        lifted,
        size,
        { x: delta.x, y: 0, z: delta.z },
        solids,
        floor,
        oneWayFloor
      );
      const raisedDist = Math.hypot(raised.position.x - start.x, raised.position.z - start.z);
      if (raisedDist - traveled > TOUCH_EPSILON) {
        const landing = raised.position;
        const dropHit = earliestHit(
          bodyAabb(landing, size),
          { x: 0, y: -lift, z: 0 },
          solids,
          floor,
          oneWayFloor
        );
        let drop = dropHit ? dropHit.fraction * -lift + dropHit.normal.y * SKIN : -lift;
        if (floor !== null && landing.y + drop < floor) drop = floor - landing.y;
        // 実際に一段高いところへ着地できたときだけ採用する。
        // 同じ床へ戻った場合は採用しない（壁ずりで水平移動が二重にならないように）。
        if (landing.y + drop > position.y + TOUCH_EPSILON) {
          position = { x: landing.x, y: landing.y + drop, z: landing.z };
          onGround = true;
          stepped = true;
          hitWall = raised.hitWall;
        }
      }
    }
  }

  const snapped = snapToWalkable(
    position,
    walkables,
    walkableSnapUp,
    onGround || options.wasOnGround === true
  );
  if (snapped.onGround) {
    const candidate = { x: position.x, y: snapped.y, z: position.z };
    // 階段斜面へ引き戻すと、同じ高さの着地プレーンに体が刺さって前進できなくなる。
    if (snapped.y + TOUCH_EPSILON >= position.y || !bodyHitsSolid(candidate, size, solids)) {
      position = candidate;
      onGround = true;
    }
  }

  return {
    position,
    onGround,
    hitCeiling,
    hitWall,
    stepped,
  };
}

function intersectsCollider(box: Aabb, collider: Collider, epsilon = TOUCH_EPSILON): boolean {
  if (collider.kind === 'aabb') return aabbIntersects(box, collider.box, epsilon);
  if (collider.kind === 'obb') return aabbIntersectsObb(box, collider.obb, epsilon);
  return aabbIntersectsPrism(box, collider.prism, epsilon);
}

function bodyHitsSolid(feet: Vec3, size: BodySize, solids: readonly Collider[]): boolean {
  const box = bodyAabb(feet, size);
  for (const collider of solids) {
    if (intersectsCollider(box, collider)) return true;
  }
  return false;
}

/** 体の AABB が何かに埋まっているか（設置の可否判定などに使う） */
export function bodyIntersects(world: CollisionWorld, feet: Vec3, size: BodySize): boolean {
  const box = bodyAabb(feet, size);
  const candidates = world.query(box);
  for (const other of candidates) {
    if (intersectsCollider(box, other)) return true;
  }
  return false;
}

/**
 * 足元が何かに埋まっているとき、同じ XZ のまま真上の空きスペースへ逃がす。
 * ブロック上面などの立ち位置候補を低い順に試し、頭上まで空いている最初の位置を返す。
 * 埋まっていなければ（床補正のみして）そのまま返す。
 */
export function findSafeStandingPosition(
  world: CollisionWorld,
  feet: Vec3,
  size: BodySize,
  options: { floor?: number | null; maxLift?: number } = {}
): Vec3 {
  const floor = options.floor ?? null;
  const maxLift = options.maxLift ?? 256;
  const baseY = floor !== null ? Math.max(feet.y, floor) : feet.y;
  const start = { x: feet.x, y: baseY, z: feet.z };
  if (!bodyIntersects(world, start, size)) return start;

  const half = size.width / 2;
  const column: Aabb = {
    min: { x: feet.x - half, y: baseY, z: feet.z - half },
    max: { x: feet.x + half, y: baseY + maxLift + size.height, z: feet.z + half },
  };
  const colliders = world.query(column);

  const candidates = new Set<number>([baseY]);
  for (const collider of colliders) {
    const box = colliderBounds(collider);
    // 足元の水平投影と重ならない箱は立ち位置候補にしない
    if (box.max.x <= feet.x - half + TOUCH_EPSILON) continue;
    if (box.min.x >= feet.x + half - TOUCH_EPSILON) continue;
    if (box.max.z <= feet.z - half + TOUCH_EPSILON) continue;
    if (box.min.z >= feet.z + half - TOUCH_EPSILON) continue;
    if (box.max.y < baseY - TOUCH_EPSILON) continue;
    if (box.max.y > baseY + maxLift + TOUCH_EPSILON) continue;
    candidates.add(box.max.y);
  }

  const sorted = [...candidates].sort((a, b) => a - b);
  for (const y of sorted) {
    const pos = { x: feet.x, y, z: feet.z };
    if (!bodyIntersects(world, pos, size)) return pos;
  }

  // 候補が尽きたときは細かく持ち上げて空きを探す（斜め板などの保険）
  for (let y = baseY; y <= baseY + maxLift; y += 0.25) {
    const pos = { x: feet.x, y, z: feet.z };
    if (!bodyIntersects(world, pos, size)) return pos;
  }
  return { x: feet.x, y: baseY + maxLift, z: feet.z };
}

/** `boxes` の中で AABB と重なるものがあるか */
export function overlapsAny(box: Aabb, boxes: readonly Aabb[]): boolean {
  for (const other of boxes) {
    if (aabbIntersects(box, other, TOUCH_EPSILON)) return true;
  }
  return false;
}
